/**
 * PracticeAreaTemplate — Morgan Legal Group
 *
 * Reusable hub template for every practice-area page. Driven by a single
 * `PracticeArea` content entry from `src/data/practiceAreas.ts`. Sections:
 *
 *   1.  Navbar (existing)
 *   2.  Hero — navy with watermark, eyebrow + H1 (gold-italic accent) + lead + CTAs
 *   3.  Overview — cream, editorial 2-column-ish prose
 *   4.  SubServices — grid of cards with check-bulleted scope
 *   5.  NY Facts — navy, key/value grid with gold dividers
 *   6.  Process — cream, Roman-numeral numbered steps
 *   7.  FAQ — semantic <details>/<summary> accordion (no JS needed for open/close)
 *   8.  Author byline — RM monogram + name + credentials
 *   9.  Related practice areas — 3-5 link cards
 *  10.  CTA — gold band, Schedule Consultation button
 *  11.  Footer (existing)
 *
 * Schema (LegalService + FAQPage + BreadcrumbList + Article + Speakable) is
 * injected via the existing SchemaJsonLd component.
 */
import { useEffect } from 'react';
import { Phone, Calendar, Check, ChevronDown, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { practiceAreaLabels, type PracticeArea } from '@/data/practiceAreas';
import { relatedArticlesByPractice } from '@/data/relatedArticles';
import { guidesByPractice } from '@/data/practiceGuides';
import { practiceDeepDives } from '@/data/practiceDeepDives';
import { renderQaBlocks } from '@/components/QaBlocks';
import { pageDates } from '@/data/pageDates';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

interface PracticeAreaPageProps {
  practiceArea: PracticeArea;
}

function buildSchema(pa: PracticeArea) {
  const canonical = `${SITE}${pa.url}`;
  const dates = pageDates(pa.url);
  const slugId = `${canonical}#service`;

  return [
    {
      '@context': 'https://schema.org',
      // 🔴 Здесь Service, а не LegalService. LegalService в schema.org — это
      // подтип LocalBusiness, то есть ЗАВЕДЕНИЕ, и свойств `serviceType` и
      // `provider` у него нет. Официальный проверяющий сервис 07.09 отвечал на
      // эти две строки UNKNOWN_FIELD, а на `provider` — ещё и INVALID_OBJECT.
      // Локационный шаблон делает так же (LocationTemplate.tsx: 'Service').
      '@type': 'Service',
      '@id': slugId,
      name: `${practiceAreaLabels[pa.slug]?.label ?? pa.heroHeadline} in New York`,
      serviceType: practiceAreaLabels[pa.slug]?.label ?? pa.heroHeadline,
      url: canonical,
      description: pa.metaDescription,
      areaServed: [
        { '@type': 'City', name: 'New York' },
        { '@type': 'AdministrativeArea', name: 'Manhattan' },
        { '@type': 'AdministrativeArea', name: 'Brooklyn' },
        { '@type': 'AdministrativeArea', name: 'Queens' },
        { '@type': 'AdministrativeArea', name: 'Bronx' },
        { '@type': 'AdministrativeArea', name: 'Staten Island' },
      ],
      provider: { '@id': `${SITE}/#organization` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: pa.faq.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Practice Areas',
          item: `${SITE}/practice-areas/`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: practiceAreaLabels[pa.slug]?.label ?? pa.heroHeadline,
          item: canonical,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: pa.title,
      description: pa.metaDescription,
      author: {
        '@type': 'Person',
        name: pa.author.name,
        jobTitle: pa.author.title,
      },
      // Google требует у Article картинку. Своей у страницы практики нет —
      // берётся общая, та же, что в Open Graph.
      image: `${SITE}/og-image-home.jpg`,
      publisher: { '@id': `${SITE}/#organization` },
      datePublished: dates.published,
      dateModified: dates.modified,
      mainEntityOfPage: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: canonical,
      name: pa.title,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.lead', '[data-speakable]'],
      },
    },
  ];
}

function HeroBanner({ pa }: { pa: PracticeArea }) {
  const watermark = (practiceAreaLabels[pa.slug]?.label ?? pa.heroHeadline).toUpperCase();
  return (
    <section className="relative bg-navy pt-28 lg:pt-32 pb-20 lg:pb-28 overflow-hidden">
      {/* Background watermark text */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap" style={{ opacity: 0.03 }}>
          {watermark}
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
          <a href="/" className="hover:text-gold transition-colors">Home</a>
          <span className="mx-2 text-gold/60">/</span>
          <a href="/practice-areas/" className="hover:text-gold transition-colors">Practice Areas</a>
          <span className="mx-2 text-gold/60">/</span>
          <span className="text-white/80">{practiceAreaLabels[pa.slug]?.label ?? pa.heroHeadline}</span>
        </nav>

        <div className="max-w-3xl">
          <div className="section-label mb-5 text-gold">{pa.heroEyebrow}</div>

          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-7">
            {pa.heroHeadline}
            {pa.heroHeadlineAccent ? (
              <>
                {' '}
                <span className="text-gold italic font-display">{pa.heroHeadlineAccent}</span>
              </>
            ) : null}
          </h1>

          <p className="lead font-body text-white/80 text-lg lg:text-xl leading-relaxed mb-9 max-w-2xl" data-speakable>
            {pa.heroLead}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <a href="/appointment/" className="btn-gold">
              <Calendar className="w-4 h-4" />
              Schedule Free Consultation
            </a>
            <a href="tel:8885291315" className="btn-ghost-gold inline-flex items-center gap-2 border border-gold/40 text-gold font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-gold hover:text-navy">
              <Phone className="w-4 h-4" />
              888-529-1315
            </a>
          </div>

          <div className="flex items-center gap-4 pt-6 border-t border-white/10">
            <picture className="flex-shrink-0">
              <source srcSet="/img/attorneys/russel-morgan-headshot.webp" type="image/webp" />
              <img
                src="/img/attorneys/russel-morgan-headshot.jpg"
                alt="Russel Morgan, Esq."
                width="640"
                height="640"
                loading="lazy"
                className="w-12 h-12 rounded-full object-cover border-2 border-gold/40"
              />
            </picture>
            <div>
              <p className="font-display text-white text-base font-semibold leading-tight">{pa.author.name}</p>
              <p className="font-body text-white/60 text-xs tracking-wide uppercase">{pa.author.title}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OverviewSection({ pa }: { pa: PracticeArea }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div
              className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <div className="section-label mb-4">Overview</div>
              <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-4xl font-bold leading-tight">
                {pa.overview.headline}
              </h2>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-5">
            {pa.overview.paragraphs.map((para, i) => (
              <p
                key={i}
                className={`font-body text-navy/75 text-base lg:text-lg leading-relaxed transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {para}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SubServicesGrid({ pa }: { pa: PracticeArea }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="section-label mb-4">What We Handle</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-4xl font-bold leading-tight mb-4">
            Full-Scope <span className="text-gold italic">Representation</span>
          </h2>
          <p className="font-body text-navy/65 text-base lg:text-lg leading-relaxed">
            Every {practiceAreaLabels[pa.slug]?.label.toLowerCase() ?? 'matter'} is different. Below are the
            services we routinely deliver — bundled or à la carte, depending on what your case needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {pa.subServices.map((svc, i) => (
            <article
              key={svc.title}
              className={`service-card transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="absolute top-0 left-0 w-12 h-0.5 bg-gold" />
              <h3 className="font-display text-navy text-xl lg:text-2xl font-bold leading-snug mb-3 mt-2">
                {svc.title}
              </h3>
              <p className="font-body text-navy/70 text-sm lg:text-base leading-relaxed mb-4">
                {svc.description}
              </p>
              {svc.bullets && svc.bullets.length > 0 ? (
                <ul className="space-y-2">
                  {svc.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 font-body text-sm text-navy/80">
                      <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function NyFactsSection({ pa }: { pa: PracticeArea }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span className="absolute -top-6 -left-6 font-display text-[10rem] lg:text-[14rem] font-bold leading-none text-white whitespace-nowrap" style={{ opacity: 0.025 }}>
          NEW YORK
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="section-label mb-4 text-gold">By The Numbers</div>
          <h2 className="font-display text-white text-3xl sm:text-4xl lg:text-4xl font-bold leading-tight mb-3">
            {pa.nyFacts.headline}
          </h2>
          <p className="font-body text-white/65 text-base lg:text-lg leading-relaxed">{pa.nyFacts.intro}</p>
        </div>

        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
          {pa.nyFacts.facts.map((f, i) => (
            <div
              key={f.label}
              className={`flex items-baseline justify-between gap-6 py-5 border-b border-gold/20 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
              }`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <dt className="font-body text-white/60 text-sm lg:text-base uppercase tracking-wide">{f.label}</dt>
              <dd className="font-display text-gold text-base lg:text-lg font-semibold text-right">{f.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function ProcessSteps({ pa }: { pa: PracticeArea }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="section-label mb-4">Our Process</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-4xl font-bold leading-tight mb-4">
            {pa.process.headline}
          </h2>
          <p className="font-body text-navy/65 text-base lg:text-lg leading-relaxed">{pa.process.intro}</p>
        </div>

        <ol className="space-y-6 lg:space-y-8">
          {pa.process.steps.map((step, i) => (
            <li
              key={step.title}
              className={`grid grid-cols-[auto_1fr] gap-5 lg:gap-8 items-start transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 lg:w-16 lg:h-16 border-2 border-gold flex items-center justify-center bg-white">
                  <span className="font-display text-gold text-lg lg:text-xl font-bold">{ROMAN[i] ?? i + 1}</span>
                </div>
                {i < pa.process.steps.length - 1 ? (
                  <div className="w-px flex-1 bg-gold/30 mt-2 min-h-[2rem]" />
                ) : null}
              </div>
              <div className="pb-6">
                <h3 className="font-display text-navy text-xl lg:text-2xl font-bold leading-snug mb-2">{step.title}</h3>
                <p className="font-body text-navy/70 text-sm lg:text-base leading-relaxed max-w-2xl">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function FaqAccordion({ pa }: { pa: PracticeArea }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className={`lg:sticky lg:top-28 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="section-label mb-4">FAQ</div>
              <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-4xl font-bold leading-tight mb-5">
                Questions, <span className="text-gold italic">Answered</span>
              </h2>
              <p className="font-body text-navy/65 text-base leading-relaxed mb-7">
                The questions clients ask most when they pick up the phone. Still need more? Call or schedule a
                consultation — we&apos;re happy to walk through your specific facts.
              </p>
              <a href="/appointment/" className="btn-navy">
                Ask Us Directly
              </a>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-3">
            {pa.faq.map((f, i) => (
              <details
                key={f.question}
                className={`group bg-cream border border-gold/20 hover:border-gold/40 transition-all duration-300 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                }`}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <summary className="flex items-start justify-between gap-4 p-5 lg:p-6 cursor-pointer list-none">
                  <span className="font-display text-navy text-base lg:text-lg font-semibold leading-snug group-open:text-gold transition-colors">
                    {f.question}
                  </span>
                  <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center border border-navy/20 text-navy/60 group-open:bg-gold group-open:border-gold group-open:text-white group-open:rotate-180 transition-all mt-0.5">
                    <ChevronDown className="w-4 h-4" />
                  </span>
                </summary>
                <div className="px-5 lg:px-6 pb-5 lg:pb-6">
                  <div className="w-8 h-0.5 bg-gold mb-3" />
                  <p className="font-body text-navy/75 text-sm lg:text-base leading-relaxed">{f.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Развёрнутые разборы, переехавшие с главной 30.08.2026 (пункт 19 ревью).
 *
 * Раскрыты по умолчанию, а не спрятаны в аккордеон: на главной они были
 * свёрнуты, и там это оправдывалось длиной списка из четырнадцати штук. Здесь
 * их два-пять на страницу, и это основное содержание страницы, ради которого
 * на неё и приходят из поиска.
 */
function DeepDives({ pa }: { pa: PracticeArea }) {
  const dives = practiceDeepDives[pa.slug];
  if (!dives || !dives.length) return null;
  return (
    <section className="py-20 lg:py-28 bg-cream border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          <div className="section-label mb-4">In Depth</div>
          <div className="space-y-14">
            {dives.map((d) => (
              <article key={d.question}>
                <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold leading-snug mb-5">
                  {d.question}
                </h2>
                {renderQaBlocks(d.blocks, true)}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AuthorByline({ pa }: { pa: PracticeArea }) {
  return (
    <section className="bg-cream pb-20 lg:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-navy text-white p-7 lg:p-10 flex flex-col sm:flex-row items-start gap-6 lg:gap-8 max-w-4xl mx-auto">
          <picture className="flex-shrink-0">
            <source srcSet="/img/attorneys/russel-morgan-headshot.webp" type="image/webp" />
            <img
              src="/img/attorneys/russel-morgan-headshot.jpg"
              alt="Russel Morgan, Esq."
              width="640"
              height="640"
              loading="lazy"
              className="w-20 h-20 lg:w-24 lg:h-24 rounded-full object-cover border-2 border-gold/50"
            />
          </picture>
          <div className="flex-1">
            <p className="font-body text-gold text-xs uppercase tracking-[0.2em] mb-2">Article Author</p>
            <h3 className="font-display text-white text-xl lg:text-2xl font-bold mb-1">{pa.author.name}</h3>
            <p className="font-body text-white/80 text-sm lg:text-base mb-2">{pa.author.title}</p>
            <p className="font-body text-white/60 text-xs lg:text-sm leading-relaxed">{pa.author.credentials}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RelatedAreas({ pa }: { pa: PracticeArea }) {
  if (!pa.related || pa.related.length === 0) return null;
  return (
    <section className="py-20 lg:py-28 bg-white border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 lg:mb-14">
          <div className="section-label mb-4">Related Practices</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight">
            Connected <span className="text-gold italic">Practice Areas</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pa.related.map((slug) => {
            const info = practiceAreaLabels[slug];
            const href = `/${slug}/`;
            return (
              <a
                key={slug}
                href={href}
                className="group block p-6 bg-cream border border-navy/10 hover:border-gold hover:shadow-lg transition-all duration-300"
              >
                <div className="w-10 h-0.5 bg-gold mb-4 transition-all duration-300 group-hover:w-16" />
                <h3 className="font-display text-navy text-lg lg:text-xl font-bold mb-2 group-hover:text-gold transition-colors">
                  {info?.label ?? slug}
                </h3>
                <p className="font-body text-navy/65 text-sm leading-relaxed mb-4">
                  {info?.blurb ?? 'Related practice area at Morgan Legal Group.'}
                </p>
                <span className="inline-flex items-center gap-1 font-body text-xs uppercase tracking-wider text-gold font-semibold">
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Углублённые разборы этой практики. До появления этого блока сорок семь
 * страниц сборки не имели ни одной входящей ссылки — существовали и были
 * недостижимы изнутри сайта. Список выводится из данных самих материалов
 * (`@/data/practiceGuides`), а не перечисляется здесь.
 */
function PracticeGuides({ pa }: { pa: PracticeArea }) {
  const guides = guidesByPractice[pa.slug];
  if (!guides || guides.length === 0) return null;
  return (
    <section className="py-16 lg:py-20 bg-white border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-8 lg:mb-10">
          <div className="section-label mb-4">Go Deeper</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight">
            In-Depth <span className="text-gold italic">Guides</span>
          </h2>
          <p className="font-body text-navy/70 text-base leading-relaxed mt-4">
            {/*
              🔴 Здесь стояло `pa.title.toLowerCase()`, а `title` — это МЕТА-заголовок
              целиком. В тело страницы уезжало «…the questions estate planning
              attorney nyc | wills, trusts, poa | morgal legal clients ask most
              often». Берём короткое название практики, а не строку для выдачи:
              одно поле играло две роли. Найдено аудитом 03.09.2026.
            */}
            Detailed answers to the questions {(practiceAreaLabels[pa.slug]?.label ?? pa.slug.replace(/-/g, ' ')).toLowerCase()} clients ask most often in New York.
          </p>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {guides.map((g) => (
            <li key={g.url}>
              <a
                href={g.url}
                className="group flex items-start gap-3 p-5 h-full bg-cream border border-navy/10 hover:border-gold hover:shadow-lg transition-all duration-300"
              >
                <ArrowRight
                  className="w-4 h-4 text-gold mt-1 shrink-0 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
                <span className="font-body text-navy text-base leading-snug group-hover:text-gold transition-colors">
                  {g.title}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function RelatedArticles({ pa }: { pa: PracticeArea }) {
  const articles = relatedArticlesByPractice[pa.slug];
  if (!articles || articles.length === 0) return null;
  return (
    <section className="py-16 lg:py-20 bg-cream border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-8 lg:mb-10">
          <div className="section-label mb-4">From Our Insights</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight">
            Related <span className="text-gold italic">Articles</span>
          </h2>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {articles.map((a) => (
            <li key={a.url}>
              <a
                href={a.url}
                className="group flex items-start gap-3 p-5 bg-white border border-navy/10 hover:border-gold hover:shadow-lg transition-all duration-300"
              >
                <ArrowRight className="w-4 h-4 text-gold mt-1 shrink-0 transition-transform group-hover:translate-x-1" />
                <span className="font-body text-navy text-base leading-snug group-hover:text-gold transition-colors">
                  {a.title}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function CtaBand({ pa }: { pa: PracticeArea }) {
  return (
    <section className="bg-gold py-16 lg:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
          {pa.cta.headline}
        </h2>
        <p className="font-body text-navy/85 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
          {pa.cta.body}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href="/appointment/" className="btn-navy">
            <Calendar className="w-4 h-4" />
            Schedule Free Consultation
          </a>
          <a
            href="tel:8885291315"
            className="inline-flex items-center gap-2 border-2 border-navy text-navy font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-navy hover:text-gold"
          >
            <Phone className="w-4 h-4" />
            888-529-1315
          </a>
        </div>
      </div>
    </section>
  );
}

function useHead(pa: PracticeArea) {
  useEffect(() => {
    const canonical = `${SITE}${pa.url}`;
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: pa.title, description: pa.metaDescription, url: canonical });
    }
  }, [pa]);
}

export default function PracticeAreaTemplate({ practiceArea }: PracticeAreaPageProps) {
  const schema = buildSchema(practiceArea);
  useHead(practiceArea);
  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main id="practice-area">
        <HeroBanner pa={practiceArea} />
        <OverviewSection pa={practiceArea} />
        <SubServicesGrid pa={practiceArea} />
        <NyFactsSection pa={practiceArea} />
        <ProcessSteps pa={practiceArea} />
        <FaqAccordion pa={practiceArea} />
        <DeepDives pa={practiceArea} />
        <AuthorByline pa={practiceArea} />
        <RelatedAreas pa={practiceArea} />
        <PracticeGuides pa={practiceArea} />
        <RelatedArticles pa={practiceArea} />
        <CtaBand pa={practiceArea} />
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
